import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Row,
  Form,
  DatePicker,
  Input,
  Checkbox,
} from 'antd';
import Select from 'components/Select';
import PaginatedSelect from 'components/PaginatedSelect';
import { HideToPreserve } from 'components/Steps';
import { getCategoriasDeQuejas, getMotivosDeQuejas, getSubmotivos } from 'api/solicitudes/quejas';
import { getPlantillasNoIndexadas, getTiposDeVisitasDomiciliarias } from 'api/tramites';
import { getDepartamentos, getDependencias } from 'api/configuracion';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCategoriasDeQuejas,
  selectMotivosDeQuejas,
  selectSubMotivosDeQuejas,
  selectTiposDeVisitasDomiciliarias,
} from 'store/catalogos';
import { useLocation } from 'react-router';

const PlantillasSelect = ({
  label,
  name,
  labelProp,
  onChange,
  required,
}) => {
  const form = Form.useFormInstance();
  const [data, setData] = useState({ results: [] });
  const [loading, setLoading] = useState(false);
  const plantillasRef = useRef();
  const location = useLocation();

  useEffect(() => {
    if (location?.state?.plantilla) {
      const { plantilla } = location.state;
      setData({ results: [plantilla] });
      form.setFieldsValue({ plantilla_de_tramites: plantilla.id });
    }
  }, []);

  const fetchPlantillas = async (page = 1) => {
    const { plantilla } = location.state;
    if (!plantilla) {
      setLoading(true);
      const response = await getPlantillasNoIndexadas({ page });
      setLoading(false);
      setData(response);
    }
  };

  return (
    <Form.Item
      label={label}
      name={name}
      rules={[{ required }]}
    >
      <PaginatedSelect
        loading={loading}
        onChange={onChange}
        onClear={() => fetchPlantillas()}
        dataSource={data?.results?.length ? data : plantillasRef.current}
        labelProp={labelProp}
        usingExternalDataSource={!!location?.state?.plantilla}
        disabled={!!location?.state?.plantilla}
        pagination={{
          getter: async (...args) => {
            setLoading(true);
            const _data = await getPlantillasNoIndexadas({
              ...args[0],
              [labelProp]: args[0].q,
            });
            setLoading(false);
            return _data;
          },
          setter: (dataSource) => {
            plantillasRef.current = dataSource;
          },
          config: { auto: !location?.state?.plantilla },
        }}
      />
    </Form.Item>
  );
};

PlantillasSelect.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  labelProp: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
};

PlantillasSelect.defaultProps = {
  label: 'Plantillas',
  name: 'plantilla',
  labelProp: 'nombre',
  onChange: () => null,
  required: true,
};

const UnidadesOperativasSelect = ({
  label,
  name,
  labelProp,
  onChange,
  required,
}) => {
  const form = Form.useFormInstance();
  const [data, setData] = useState({ results: [] });
  const [loading, setLoading] = useState(false);
  const unidadesOperativasRef = useRef();
  const location = useLocation();

  useEffect(() => {
    if (location?.state?.plantilla) {
      const { plantilla } = location.state;
      setData({ results: [plantilla.departamentos[0].unidad_operativa] });
      form.setFieldsValue({ [name]: plantilla.departamentos[0].unidad_operativa.id });
    }
  }, []);

  const fetchUnidadesOperativas = async (page = 1) => {
    const { plantilla } = location.state;
    if (!plantilla) {
      setLoading(true);
      const response = await getDependencias({ page });
      setLoading(false);
      setData(response);
    }
  };

  return (
    <Form.Item
      label={label}
      name={name}
      rules={[{ required }]}
    >
      <PaginatedSelect
        loading={loading}
        onChange={onChange}
        onClear={() => fetchUnidadesOperativas()}
        usingExternalDataSource={!!location?.state?.plantilla}
        disabled={!!location?.state?.plantilla}
        dataSource={data?.results?.length ? data : unidadesOperativasRef.current}
        labelProp={labelProp}
        pagination={{
          getter: async (...args) => {
            setLoading(true);
            const _data = await getDependencias({
              ...args[0],
              [labelProp]: args[0].q,
            });
            setLoading(false);
            return _data;
          },
          setter: (dataSource) => {
            unidadesOperativasRef.current = dataSource;
          },
          config: { auto: !location?.state?.plantilla },
        }}
      />
    </Form.Item>
  );
};

UnidadesOperativasSelect.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  labelProp: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
};

UnidadesOperativasSelect.defaultProps = {
  label: 'Unidades Operativas',
  name: 'unidad_operativa',
  labelProp: 'nombre',
  onChange: () => null,
  required: true,
};

const DepartamentosSelect = ({
  label,
  name,
  labelProp,
  onChange,
  required,
}) => {
  const form = Form.useFormInstance();
  const [data, setData] = useState({ results: [] });
  const [loading, setLoading] = useState(false);
  const unidadesOperativasRef = useRef();
  const location = useLocation();

  useEffect(() => {
    if (location?.state?.plantilla) {
      const { plantilla } = location.state;
      setData({ results: [plantilla.departamentos[0].unidad_operativa] });
      form.setFieldsValue({ [name]: plantilla.departamentos[0].unidad_operativa.id });
    }
  }, []);

  const fetchDepartamentos = async (page = 1) => {
    const { plantilla } = location.state;
    if (!plantilla) {
      setLoading(true);
      const response = await getDepartamentos({ page });
      setLoading(false);
      setData(response);
    }
  };

  return (
    <Form.Item
      label={label}
      name={name}
      rules={[{ required }]}
    >
      <PaginatedSelect
        loading={loading}
        onChange={onChange}
        onClear={() => fetchDepartamentos()}
        usingExternalDataSource={!!location?.state?.plantilla}
        disabled={!!location?.state?.plantilla}
        dataSource={data?.results?.length ? data : unidadesOperativasRef.current}
        labelProp={labelProp}
        pagination={{
          getter: async (...args) => {
            setLoading(true);
            const _data = await getDepartamentos({
              ...args[0],
              [labelProp]: args[0].q,
            });
            setLoading(false);
            return _data;
          },
          setter: (dataSource) => {
            unidadesOperativasRef.current = dataSource;
          },
          config: { auto: !location?.state?.plantilla },
        }}
      />
    </Form.Item>
  );
};

DepartamentosSelect.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  labelProp: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
};

DepartamentosSelect.defaultProps = {
  label: 'Unidades Operativas',
  name: 'unidad_operativa',
  labelProp: 'nombre',
  onChange: () => null,
  required: true,
};

const voOptions = [
  { descripcion: 'Trámite', id: true },
  { descripcion: 'Visita', id: false },
];

const InformacionGeneral = ({ visible }) => {
  const form = Form.useFormInstance();
  const esTramite = Form.useWatch('es_tramite', form);

  const dispatch = useDispatch();
  const motivos = useSelector(selectMotivosDeQuejas);
  const cartegorias = useSelector(selectCategoriasDeQuejas);
  const subMotivos = useSelector(selectSubMotivosDeQuejas);
  const tiposDeVisitasDomiciliarias = useSelector(selectTiposDeVisitasDomiciliarias);
  const location = useLocation();

  const [filteredMotivos, setFilteredMotivos] = useState([]);
  const [filteredSubMotivos, setFilteredSubMotivos] = useState([]);
  const [otroTramite, setOtroTramite] = useState(false);
  const [otraOficina, setOtraOficina] = useState(false);

  const onChangeCategoria = (value) => {
    form.resetFields(['motivo_de_la_queja']);
    if (!value) {
      setFilteredSubMotivos([]);
      setFilteredMotivos([]);
      return;
    }
    const filtered = motivos.filter((motivo) => motivo.categoria_de_la_queja === value);
    const subFiltered = subMotivos.filter((subMotivo) => subMotivo.categoria_de_la_queja === value);
    setFilteredMotivos(filtered);
    setFilteredSubMotivos(subFiltered);
  };
  const onChange = async (value) => {
    if (value) {
      await Promise.all([
        getMotivosDeQuejas(dispatch),
        getCategoriasDeQuejas(dispatch),
        getSubmotivos(dispatch),
      ]);
    } else {
      await getTiposDeVisitasDomiciliarias(dispatch);
    }
  };
  useEffect(() => {
    if (location?.state?.plantilla) {
      form.setFieldsValue({
        es_tramite: true,
      });
      onChangeCategoria(form.getFieldValue('categoria_de_la_queja'));
    }
  }, []);

  const rules = {
    required: [{ required: true }],
  };

  const onChangeMotivo = (value) => {
    form.resetFields(['submotivo_de_la_queja']);
    if (!value) {
      setFilteredSubMotivos([]);
      return;
    }
    const filtered = subMotivos.filter((subMotivo) => subMotivo.motivo_de_la_queja === value);
    setFilteredSubMotivos(filtered);
  };

  return (
    <HideToPreserve visible={visible}>
      <Row gutter={10}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="¿Trámite o visita?"
            name="es_tramite"
            rules={rules.required}
          >
            <Select
              options={voOptions}
              disabled={!!location.state?.plantilla}
              onChange={onChange}
            />
          </Form.Item>
        </Col>
        {esTramite && (
          <>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label="¿Qué desea realizar?"
                name="categoria_de_la_queja"
                rules={rules.required}
              >
                <Select
                  options={cartegorias}
                  onChange={onChangeCategoria}
                  disabled={!!location.state?.plantilla}
                />
              </Form.Item>
            </Col>
            {!!filteredMotivos?.length && (
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label="Tipo de protesta"
                name="motivo_de_la_queja"
                rules={rules.required}
              >
                <Select options={filteredMotivos} onChange={onChangeMotivo} />
              </Form.Item>
            </Col>
            )}
            {!!filteredSubMotivos?.length && (
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label="Motivo"
                name="submotivo_de_la_queja"
                rules={rules.required}
              >
                <Select options={filteredSubMotivos} />
              </Form.Item>
            </Col>
            )}
          </>
        )}
        {!esTramite && esTramite !== undefined && (
          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label="Tipo de visita"
              name="tipo_de_visita_domiciliaria"
              rules={rules.required}
            >
              <Select
                options={tiposDeVisitasDomiciliarias}
                getLabel={(option) => option.nombre}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Row gutter={10}>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="Fecha de los hechos"
            name="fecha_de_los_hechos"
            rules={rules.required}
          >
            <DatePicker
              disabledDate={(current) => current.isAfter(new Date())}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="Hora aproximada de los hechos"
            name="hora_de_los_hechos"
            rules={rules.required}
          >
            <DatePicker.TimePicker placeholder="" use12Hours format="h:mm a" />
          </Form.Item>
        </Col>
        <Col xs={12} sm={12} md={location?.state?.plantilla ? 24 : 16}>
          <PlantillasSelect
            label="Tramite/Servicio que quería realizar"
            name="plantilla_de_tramites"
            required={!otroTramite}
          />
        </Col>
        {!location?.state?.plantilla && (
          <Col xs={12} sm={12} md={8}>
            <Form.Item
              label=" "
              name="otro_tramite"
            >
              <Checkbox
                checked={otroTramite}
                onChange={(e) => setOtroTramite(e.target.checked)}
              >
                Otro Trámite/Servicio
              </Checkbox>
            </Form.Item>
          </Col>
        )}
        {otroTramite && (
          <Col span={24}>
            <Form.Item
              label="Ingrese el nombre del trámite o servicio que quería realizar"
              name="tramite_de_queja"
              rules={rules.required}
            >
              <Input />
            </Form.Item>
          </Col>
        )}
        <Col xs={12} sm={12} md={location?.state?.plantilla ? 24 : 16}>
          <UnidadesOperativasSelect
            label="Dependencia en donde sucedieron los hechos"
            labelProp="descripcion"
            name="unidades_operativas"
            required={!otraOficina}
          />
        </Col>
        {!location?.state?.plantilla && (
          <Col xs={12} sm={12} md={8}>
            <Form.Item
              label=" "
              name="otra_oficina"
            >
              <Checkbox
                checked={otraOficina}
                onChange={(e) => setOtraOficina(e.target.checked)}
              >
                Otra Oficina
              </Checkbox>
            </Form.Item>
          </Col>
        )}
        {otraOficina && (
          <Col span={24}>
            <Form.Item
              label="Nombre de la oficina en donde sucedieron los hechos"
              name="oficina_de_queja"
              rules={rules.required}
            >
              <Input />
            </Form.Item>
          </Col>
        )}
        <Col span={24}>
          <DepartamentosSelect
            label="Unidad Administrativa"
            name="unidad_responsable"
            labelProp="descripcion"
          />
        </Col>
        <Col span={24}>
          <Form.Item
            label="Describa el motivo de los hechos"
            name="objeto_de_alteracion"
            rules={rules.required}
          >
            <Input.TextArea autoSize={{ minRows: 4, maxRows: 4 }} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label="Detalle de los hechos"
            name="detalle_de_hechos"
            rules={rules.required}
          >
            <Input.TextArea autoSize={{ minRows: 4, maxRows: 4 }} />
          </Form.Item>
        </Col>
      </Row>
    </HideToPreserve>
  );
};

InformacionGeneral.propTypes = {
  visible: PropTypes.bool,
};

InformacionGeneral.defaultProps = {
  visible: false,
};

export default InformacionGeneral;
